/** O pagină întoarsă de API: rândurile ei plus meta-ul cererii. */
export type PaginaDocumente = { data?: any[]; meta?: any };

/**
 * Adună TOATE paginile unei liste paginate. Ținută separat de React ca să fie
 * testabilă direct, fără rețea.
 *
 * FĂRĂ PLAFON de pagini, deliberat: un plafon ar reintroduce exact problema pe care o
 * rezolvă (perioade lungi — un an, mai mulți ani). Bucla se oprește când vine o pagină
 * incompletă sau când s-a adunat `meta.total`; a doua condiție face ca limita de pagini
 * a serverului (`PAGINATION_MAX_PAGE`) să rămână de neatins în practică.
 *
 * Dacă o pagină ULTERIOARĂ eșuează, păstrăm ce am adunat și raportăm prin `meta.incomplet`
 * în loc să pice toată lista — un ecran gol ascunde mai mult decât o listă parțială
 * anunțată. O eroare pe PRIMA pagină urcă normal: acolo chiar nu avem ce arăta.
 */
export async function adunaToatePaginile(
  ceruPagina: (pagina: number) => Promise<PaginaDocumente>,
  limit: number,
): Promise<{ data: any[]; meta: any }> {
  const toate: any[] = [];
  // Un rând adăugat între cereri împinge ultimul rând al paginii pe pagina următoare: fără deduplicare ar apărea de
  // două ori (cheie React dublă, sume duble), iar oprirea la `meta.total` ar pierde ultimul rând.
  const vazute = new Set<unknown>();
  let meta: any = null;
  let pagina = 1;
  let incomplet: string | null = null;
  for (;;) {
    let res: PaginaDocumente;
    try {
      res = await ceruPagina(pagina);
    } catch (e: any) {
      if (pagina === 1) throw e;
      incomplet = e?.message || 'Nu s-au putut încărca toate paginile.';
      break;
    }
    const lot = res?.data || [];
    if (!meta) meta = res?.meta ?? null;
    for (const r of lot) {
      const id = r?.id;
      if (id != null) {
        if (vazute.has(id)) continue;
        vazute.add(id);
      }
      toate.push(r);
    }
    if (lot.length < limit) break;
    const total = Number(meta?.total);
    if (Number.isFinite(total) && toate.length >= total) break;
    pagina += 1;
  }
  return {
    data: toate,
    meta: { ...(meta || {}), total: meta?.total ?? toate.length, loaded: toate.length, incomplet },
  };
}
